import type {Moment} from 'moment-timezone';

import type {Time} from 'web-app/react/components/form-elements/time/time';
import type {
    GetPurchaseForModalQuery,
    GetSessionsAndProductsQuery,
} from 'web-app/react/modals-container/purchase-modal/__generated__/queries.api-types';
import type {PurchaseInput} from 'web-app/util/graphql/api-types';
import type {UnbookableSessionFragment} from 'web-app/react/bookings-finance/graphql/prevent-double-booking/fragments/__generated__/unbookable-sessions.fragment.api-types';
import type {NewPurchaseConflictMessageFragment} from 'web-app/react/bookings-finance/graphql/prevent-double-booking/fragments/__generated__/new-purchase-conflict-message.fragment.api-types';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {type GetAutomaticPricingForPurchasesQuery} from '../__generated__/queries.api-types';

export type ProductVersion = GetSessionsAndProductsQuery['finance']['revenueItems']['productVersions'][number];

export type SessionVersion = GetSessionsAndProductsQuery['finance']['revenueItems']['sessionVersions'][number];

export type WithDay = {
    day: string;
};

export type Product = Omit<ProductVersion, 'pricing' | 'productAvailabilities'> & {
    pricing: ProductVersion['pricing'][number];
    availability: ProductVersion['productAvailabilities'][number];
};

export type Session = Omit<SessionVersion, 'pricing' | 'sessionAvailabilities'> & {
    pricing: SessionVersion['pricing'][number];
    availability: SessionVersion['sessionAvailabilities'][number];
    sessionConflict?: NewPurchaseConflictMessageFragment[];
    unbookableSession?: UnbookableSessionFragment[];
};

type Identifiable<T> = T & {
    id: string;
};

export type ProductFormValue = Identifiable<{
    item: Product;
    date: Moment;
    quantity: number;
    range?: undefined;
}>;

export type SessionFormValue = Identifiable<{
    item: Session;
    date: Moment;
    quantity?: undefined;
    range: {
        from: Time;
        to: Time;
    };
}>;

export type AddProductOrSessionPayload = {
    quantity?: number;
    range?: {
        from: Time;
        to: Time;
    };
};

export type FormValue = ProductFormValue | SessionFormValue;

export const isProductFormValue = (item: FormValue): item is ProductFormValue => hasValue(item.quantity);

/**
 * It's necessary to group the selected sessions or products both by date and by ID, as the same session or product on different days has the same ID, so we need to differentiate.
 * Even though the date is already stored on the FormValue object, using the date as key provides O(1) insertion/retrieval/deletion.
 */
export type State = {
    [date: string]: Array<FormValue>;
};

export type AddSessionAction = {
    type: 'ADD_SESSION';
    payload: Omit<SessionFormValue, 'id'>;
};

export type AddProductAction = {
    type: 'ADD_PRODUCT';
    payload: Omit<ProductFormValue, 'id'>;
};

export type UpdateSessionRangeAction = {
    type: 'UPDATE_SESSION_RANGE';
    payload: {
        id: string;
        date: Moment;
        range: {
            from?: Time;
            to?: Time;
        };
    };
};

export type UpdateProductQuantityAction = {
    type: 'UPDATE_PRODUCT_QUANTITY';
    payload: {
        id: string;
        date: Moment;
        quantity: number;
    };
};

export type RemoveProductOrSessionAction = {
    type: 'REMOVE_PRODUCT_OR_SESSION';
    payload: {
        id: string;
        date: Moment;
    };
};

export type SetInitialValuesAction = {
    type: 'SET_INITIAL_VALUES';
    payload: Record<string, Array<FormValue>>;
};

export type AdjustPriceAction = {
    type: 'ADJUST_PRICE';
    payload: {
        id: string;
        price: string;
        date: Moment;
    };
};

export type Action =
    | AddSessionAction
    | AddProductAction
    | UpdateSessionRangeAction
    | UpdateProductQuantityAction
    | RemoveProductOrSessionAction
    | SetInitialValuesAction
    | AdjustPriceAction;

export type Reducer = (state: State, action: Action) => State;

export interface UpsertPurchaseInput {
    purchaseId?: string;
    childIds: Array<string>;
    items: Array<FormValue>;
    isParentBooking: boolean;
    discount?: number;
    note: string;
    noteVisibleToParents: boolean;
    shouldApprove: boolean;
}

export type PurchaseProductBooking = PurchaseInput['productBookings'][number];

export type PurchaseSessionBooking = PurchaseInput['sessionBookings'][number];

export interface ChildOption {
    label: string;
    value: string;
    imageUrl: string | undefined;
}

export type Purchase = GetPurchaseForModalQuery['finance']['purchases']['filter']['purchases'][number];

export type SessionPurchase = Purchase['sessionBookings'][number];

export type ProductPurchase = Purchase['productBookings'][number];

export type ChildPricingData =
    GetAutomaticPricingForPurchasesQuery['finance']['purchases']['automaticPricing']['children'][number];
